/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { useContinueButtonProps } from "@packages/ab";
import {
  HallowAnalyticsEvent,
  useAnalytics,
  useViewedScreen,
} from "@packages/analytics";
import { useConsent } from "@packages/consent";
import { useRequestAuth, useRequestOtp } from "@packages/sdk";
import { HallowDataTestId } from "@tools/playwright/global";
import { useState } from "react";

import { spacing } from "../../../../global/emotion";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { useAlert } from "../../_base/modals/Dialog/Dialog.emotion";
import { OTPInput } from "../forms/OTPInput/OTPInput.emotion";
import { EmailHeader } from "./AuthFlowHeader.emotion";
import { AuthLayout } from "./AuthLayout.emotion";
import type { AuthFlowProps } from "./AuthTypes.emotion";

const containerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.ml,
  justifyContent: "flex-start",
  [cssMediaQueryMinWidthTablet]: { justifyContent: "space-between" },
  padding: spacing.ml,
  width: spacing.full,
};
const buttonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column-reverse",
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  gap: spacing.ms,
  justifyContent: "flex-end",
};
const buttonPasswordStyle: Interpolation = {
  whiteSpace: "normal",
};

export const OtpAuthFlow = ({
  className = "",
  hideHeader,
  layoutCSS = {},
  onAuth,
  setFlow,
  state,
  utm,
}: AuthFlowProps) => {
  const t = useTranslations();
  const { track } = useAnalytics();
  const alert = useAlert();
  const { consent } = useConsent();
  const { mutationPost: sendOtp } = useRequestOtp();
  const { mutationOtpVerify } = useRequestAuth();
  const { phone, email, exists } = state;
  const [code, setCode] = useState<string>("");
  const [isResending, setIsResending] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const isDesktop = useIsDesktopViewport();
  const continueButtonProps = useContinueButtonProps();
  const onSubmit = async (code: string) => {
    setLoading(true);
    try {
      const data = await mutationOtpVerify.mutateAsync({
        phone,
        email,
        otp: code,
        consent,
        ...utm,
      });
      track({
        event: HallowAnalyticsEvent.EnteredEmailVerification,
        properties: { screen: "phone_verification_input", verified: true },
      });
      await onAuth?.({ data });
    } catch (error) {
      track({
        event: HallowAnalyticsEvent.EnteredEmailVerification,
        properties: { screen: "phone_verification_input", verified: false },
      });
      await alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setIsResending(true);
      await sendOtp.mutateAsync({ phone, email });
      setTimeout(() => {
        setIsResending(false);
      }, 500);
    } catch (error) {
      setIsResending(false);
      await alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
    }
  };

  const onClickPassword = () => {
    track({
      event: HallowAnalyticsEvent.TappedUsePasswordInstead,
      properties: { is_new: !exists },
    });
    setFlow("password");
  };

  useViewedScreen({ screen: "phone_verification_input" });

  return (
    <div css={containerStyle} className={className}>
      <AuthLayout
        css={layoutCSS}
        footer={
          <div key={"buttons"} css={buttonsStyle}>
            <Button
              type={"button"}
              isFullWidth={!isDesktop}
              size="m"
              variant="transparentPrimary"
              disabled={isResending || loading}
              onClick={() => handleResend()}
            >
              {t(isResending ? "code_resent" : "sms_sign_up_resent_code")}
            </Button>
            <Button
              type={"submit"}
              isFullWidth={!isDesktop}
              size="l"
              disabled={code.length !== 6 || loading}
              onClick={() => onSubmit(code)}
              {...continueButtonProps}
            >
              {t("login_title")}
            </Button>
          </div>
        }
      >
        {!hideHeader && (
          <EmailHeader
            title={t("login_title")}
            onBack={() => setFlow(phone ? "phone" : "email")}
          />
        )}
        <Text type="headline" size="m" as="h2">
          {t(phone ? "sms_sign_up_phone_number" : "email_input_headline")}
        </Text>
        <Text>
          {t(
            phone
              ? "sms_sign_up_verification_code"
              : "email_sign_up_otp_verification_code",
          )}
        </Text>
        <OTPInput
          onSubmit={(value) => onSubmit(value)}
          onChange={(value) => setCode(value)}
          size="l"
        />
        {email && (
          <Button
            onClick={onClickPassword}
            size="l"
            css={buttonPasswordStyle}
            variant="transparentPrimary"
            data-testid={HallowDataTestId.AuthUsePasswordInsteadButton}
          >
            <span>
              {t.rich("existing_account_use_password_instead", {
                b: (chunks) => <b>{chunks}</b>,
              })}
            </span>
          </Button>
        )}
      </AuthLayout>
    </div>
  );
};
